/* ===========================================================================
   BEFORE / AFTER — runtime
   ---------------------------------------------------------------------------
   Custom element sin dependencias. El estado vive en un <input type="range">:
   él aporta teclado, ratón y táctil, y este archivo solo refleja su valor en
   la variable CSS --ba-pos, que es la que recorta la capa "antes".

   Además hace un gesto de aviso la primera vez que el comparador entra en
   pantalla. Es la parte que más importa: en un antes/después mucha gente no
   descubre que la imagen se puede arrastrar, mira la foto un segundo y sigue
   bajando sin llegar a ver el "después".
   =========================================================================== */

(function () {
  'use strict';

  if (customElements.get('before-after')) return;

  function clamp(n) {
    return Math.max(0, Math.min(100, n));
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  class BeforeAfter extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('[data-ba-range]');
      if (!this.range) return;

      this.hintDone = false;
      this.hintFrame = null;

      this.onInput = this.handleInput.bind(this);
      this.onPointerDown = this.cancelHint.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);

      this.range.addEventListener('input', this.onInput);
      this.range.addEventListener('pointerdown', this.onPointerDown);
      this.range.addEventListener('keydown', this.onKeydown);

      this.apply(parseFloat(this.range.value));
      this.setupHint();
    }

    disconnectedCallback() {
      if (this.range) {
        this.range.removeEventListener('input', this.onInput);
        this.range.removeEventListener('pointerdown', this.onPointerDown);
        this.range.removeEventListener('keydown', this.onKeydown);
      }
      if (this.observer) this.observer.disconnect();
      if (this.hintFrame) cancelAnimationFrame(this.hintFrame);
    }

    handleInput() {
      this.cancelHint();
      this.apply(parseFloat(this.range.value));
    }

    /* El input tiene step="0.1" para que arrastrar con el dedo o el ratón sea
       fluido, pero ese mismo paso deja el teclado inservible: cruzar la imagen
       costaría unas 500 pulsaciones. Las flechas se atienden aquí con un salto
       utilizable, sin tocar la finura del arrastre. */
    handleKeydown(event) {
      this.cancelHint();

      var step = event.shiftKey ? 25 : 5;
      var current = parseFloat(this.range.value) || 0;
      var next = null;

      if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next = current - step;
      else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next = current + step;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = 100;
      else if (event.key === 'PageDown') next = current - 25;
      else if (event.key === 'PageUp') next = current + 25;

      if (next === null) return;
      event.preventDefault();
      next = clamp(next);
      this.range.value = next;
      this.apply(next);
    }

    apply(value) {
      var pos = clamp(isNaN(value) ? 50 : value);
      this.style.setProperty('--ba-pos', pos + '%');
      /* aria-valuetext en vez del número pelado: "62" no dice nada, y este
         control no tiene unidades que un lector de pantalla pueda anunciar. */
      this.range.setAttribute('aria-valuetext', Math.round(pos) + '% ' + (this.dataset.labelAfter || ''));
    }

    cancelHint() {
      this.hintDone = true;
      if (this.hintFrame) {
        cancelAnimationFrame(this.hintFrame);
        this.hintFrame = null;
      }
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    setupHint() {
      if (this.getAttribute('data-hint') !== 'true') return;
      if (prefersReducedMotion()) return;
      if (typeof IntersectionObserver !== 'function') return;

      var self = this;
      this.observer = new IntersectionObserver(
        function (entries) {
          if (!entries[0] || !entries[0].isIntersecting || self.hintDone) return;
          self.observer.disconnect();
          self.observer = null;
          self.runHint();
        },
        { threshold: 0.5 }
      );
      this.observer.observe(this);
    }

    /* Vaivén corto alrededor de la posición inicial. Se mueve el valor del
       input, no solo la variable CSS, para que el control no se quede
       diciendo una cosa distinta de lo que se ve. */
    runHint() {
      var self = this;
      var start = parseFloat(this.range.value) || 50;
      var amplitude = parseFloat(this.getAttribute('data-hint-amplitude')) || 14;
      var duration = 1500;
      var t0 = null;

      function step(now) {
        if (self.hintDone) return;
        if (t0 === null) t0 = now;

        var t = (now - t0) / duration;
        if (t >= 1) {
          self.range.value = start;
          self.apply(start);
          self.hintFrame = null;
          self.hintDone = true;
          return;
        }

        /* Un seno completo sale y vuelve al punto de partida; la envolvente
           (1 - t) hace que el vaivén se apague en lugar de cortarse de golpe. */
        var offset = Math.sin(t * Math.PI * 2) * amplitude * (1 - t);
        var value = clamp(start + offset);
        self.range.value = value;
        self.apply(value);
        self.hintFrame = requestAnimationFrame(step);
      }

      this.hintFrame = requestAnimationFrame(step);
    }
  }

  customElements.define('before-after', BeforeAfter);
})();
